import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlignLeft,
  ArrowLeft,
  CalendarDays,
  Clock,
  MapPin,
  Pencil,
  Tag,
  Users,
  Info,
  type LucideIcon,
} from "lucide-react";
import {
  getCreatedActivities,
  getAllActivitiesParticipants,
  acceptJoinActivity,
  rejectJoinActivity,
  deleteActivity,
} from "@/services/api/activity";
import type { ActivityInfo, ActivityParticipant } from "@/services/api/activity/types";
import { formatDateTime, toImageList } from "@/utils/activityFormat";
import { getActivityCategoryLabelKey } from "@/utils/activityCategory";
import StudentEventCarousel from "@/components/student/StudentEventCarousel";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";

const LOOKUP_PAGES = 5;

type DetailRow = { id: string; icon: LucideIcon; label: string; value: string };

const SectionCard: React.FC<{
  icon: LucideIcon;
  title?: string;
  children: React.ReactNode;
}> = ({ icon: Icon, title, children }) => (
  <section className="mt-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
    {title && (
      <h2 className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-400">
        <Icon size={14} className="shrink-0 text-[#ffa217]" />
        {title}
      </h2>
    )}
    {children}
  </section>
);

const BusinessActivityDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { activityId } = useParams<{ activityId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const stateActivity = (location.state as { activity?: ActivityInfo } | null)?.activity;

  const [activity, setActivity] = useState<ActivityInfo | null>(stateActivity ?? null);
  const [loading, setLoading] = useState(!stateActivity);
  const [notFound, setNotFound] = useState(false);
  const [participants, setParticipants] = useState<ActivityParticipant[]>([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  useEffect(() => {
    if (!stateActivity) return;
    setActivity(stateActivity);
    setNotFound(false);
    setLoading(false);
  }, [stateActivity]);

  useEffect(() => {
    if (stateActivity || !activityId) return;
    let cancelled = false;
    const lookup = async () => {
      setLoading(true);
      for (let page = 1; page <= LOOKUP_PAGES; page += 1) {
        const resp = await getCreatedActivities(page);
        const result = resp?.result;
        if (!Array.isArray(result)) break;
        const match = result.find((item) => item.id === activityId);
        if (match) {
          if (cancelled) return;
          setActivity(match);
          setLoading(false);
          return;
        }
        if (result.length < 10) break;
      }
      if (cancelled) return;
      setLoading(false);
      setNotFound(true);
    };
    lookup();
    return () => {
      cancelled = true;
    };
  }, [stateActivity, activityId]);

  useEffect(() => {
    if (!activityId) return;
    const loadParticipants = async () => {
      setLoadingParticipants(true);
      const resp = await getAllActivitiesParticipants(activityId);
      setLoadingParticipants(false);
      if (resp?.status === 0 && Array.isArray(resp.result)) {
        setParticipants(resp.result);
      }
    };
    loadParticipants();
  }, [activityId]);

  const handleAccept = async (joinId: string) => {
    const resp = await acceptJoinActivity(joinId);
    if (resp?.status === 0) {
      setParticipants((prev) =>
        prev.map((p) =>
          p.id === joinId
            ? { ...p, coordinator_accepted: 0, coordinator_rejected: 1 }
            : p,
        ),
      );
    }
  };

  const handleReject = async (joinId: string) => {
    const resp = await rejectJoinActivity(joinId);
    if (resp?.status === 0) {
      setParticipants((prev) =>
        prev.map((p) =>
          p.id === joinId
            ? { ...p, coordinator_accepted: 1, coordinator_rejected: 0 }
            : p,
        ),
      );
    }
  };

  const handleDelete = async () => {
    if (!activityId) return;
    if (!confirm(t("Are you sure you want to delete this activity?"))) return;
    const resp = await deleteActivity(activityId);
    if (resp?.status === 0) {
      navigate("/portal/activities", { replace: true });
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-6">
        <AdminListSkeleton count={3} />
      </div>
    );
  }

  if (!activity || notFound) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="Ionicons"
            iconName="alert-circle-outline"
            title={t("Event not found")}
            message={t("This event is no longer available.")}
            actionLabel={t("Back to Home")}
            onAction={() => navigate("/portal/dashboard")}
          />
        </div>
      </div>
    );
  }

  const coverImages = toImageList(activity.cdn_activity_image);
  const activityTitle = activity.title || "";
  const activityDescription = activity.description || "";
  const dateLabel = formatDateTime(activity.date, activity.time);
  const meetingLabel = formatDateTime(activity.meeting_date, activity.meeting_time);
  const deadlineLabel = formatDateTime(activity.deadline_date, activity.deadline_time);
  const categoryKey = getActivityCategoryLabelKey(activity.category);
  const categoryLabel = categoryKey ? t(categoryKey) : "";

  const detailRows: DetailRow[] = [
    dateLabel
      ? { id: "date", icon: CalendarDays, label: t("Date"), value: dateLabel }
      : null,
    meetingLabel
      ? { id: "meeting", icon: Clock, label: t("Meeting Date & Time"), value: meetingLabel }
      : null,
    deadlineLabel
      ? { id: "deadline", icon: Clock, label: t("Application Deadline"), value: deadlineLabel }
      : null,
    activity.location
      ? { id: "location", icon: MapPin, label: t("Location"), value: activity.location }
      : null,
    activity.meeting_location
      ? { id: "meeting_location", icon: MapPin, label: t("Meeting Location"), value: activity.meeting_location }
      : null,
  ].filter(Boolean) as DetailRow[];

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 pb-16">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 transition hover:text-[#ffa217]"
      >
        <ArrowLeft size={16} />
        {t("Back")}
      </button>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="p-5 pb-4">
          <h1 className="mt-2 text-2xl font-bold text-gray-900">{activityTitle}</h1>
        </div>
        {coverImages.length > 0 && (
          <div className="px-5">
            <StudentEventCarousel images={coverImages} alt={activityTitle} />
          </div>
        )}
      </div>

      {detailRows.length > 0 && (
        <SectionCard icon={Info} title={t("Details")}>
          <div className="space-y-2">
            {detailRows.map((row) => {
              const Icon = row.icon;
              return (
                <div key={row.id} className="flex items-start gap-2 text-sm text-gray-700">
                  <Icon size={16} className="mt-0.5 shrink-0 text-[#ffa217]" />
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                      {row.label}
                    </div>
                    <div className="mt-0.5 whitespace-pre-wrap break-words">{row.value}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {activityDescription && (
        <SectionCard icon={AlignLeft} title={t("Description")}>
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700">
            {activityDescription}
          </p>
        </SectionCard>
      )}

      {categoryLabel && (
        <SectionCard icon={Tag} title={t("Category")}>
          <span className="rounded-full bg-[#fff3e0] px-2.5 py-1 text-xs font-semibold text-[#ffa217]">
            {categoryLabel}
          </span>
        </SectionCard>
      )}

      <SectionCard icon={Users} title={t("Participants")}>
        {loadingParticipants ? (
          <AdminListSkeleton count={2} />
        ) : participants.length === 0 ? (
          <p className="text-sm text-gray-500">{t("No participants yet")}</p>
        ) : (
          <div className="space-y-2">
            {participants.map((p) => {
              const userInfo = p.user_info || {};
              const accepted = p.coordinator_accepted === 0 && p.coordinator_rejected === 1;
              const rejected = p.coordinator_accepted === 1 && p.coordinator_rejected === 0;
              return (
                <div key={p.id} className="flex items-center justify-between rounded-xl border border-gray-100 p-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{userInfo.username || userInfo.university_email}</div>
                    <div className="text-xs text-gray-500">{userInfo.university_email}</div>
                  </div>
                  <div className="flex gap-2">
                    {!accepted && !rejected && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleAccept(p.id)}
                          className="rounded-lg bg-green-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-600"
                        >
                          {t("Accept")}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReject(p.id)}
                          className="rounded-lg bg-red-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-600"
                        >
                          {t("Reject")}
                        </button>
                      </>
                    )}
                    {accepted && <span className="text-xs font-semibold text-green-600">{t("Accepted")}</span>}
                    {rejected && <span className="text-xs font-semibold text-red-600">{t("Rejected")}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <div className="mt-4 flex gap-3">
        <Link
          to={`/portal/activities/${activity.id}/edit`}
          state={{ activity }}
          className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-700 transition hover:border-[#ffa217] hover:text-[#ffa217]"
        >
          <Pencil size={16} />
          {t("Edit")}
        </Link>
        <button
          type="button"
          onClick={handleDelete}
          className="rounded-xl bg-red-500 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-600"
        >
          {t("Delete")}
        </button>
      </div>
    </div>
  );
};

export default BusinessActivityDetailsPage;
